import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  Button,
  List,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  message,
  Empty,
} from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { api } from '../lib/api';
import type { Organization } from '../types';

const { Title, Text } = Typography;

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const data = await api.organizations.list();
      const orgs = data.organizations || [];
      setOrganizations(orgs);
      if (orgs.length === 1 && orgs[0].role !== 'MEMBER') {
        navigate(`/organizations/${orgs[0].id}`, { replace: true });
      }
    } catch (error: any) {
      message.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (values: { name: string; description?: string }) => {
    try {
      setCreating(true);
      const data = await api.organizations.create(values);
      message.success('Organization created successfully');
      setOrganizations([...organizations, data.organization]);
      setModalOpen(false);
      form.resetFields();
      navigate(`/organizations/${data.organization.id}`);
    } catch (error: any) {
      message.error(error.message || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>Organization</Title>
            <Text type="secondary">
              {organizations.length > 0
                ? 'Manage your team and assigments'
                : 'Create your organization and manage your team and assignments'}
            </Text>
          </div>

        </div>

        <Card loading={loading}>
          {organizations.length === 0 ? (
            <Empty
              image={<TeamOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
              description="No organization yet"
            >
              <Button type="primary" onClick={() => setModalOpen(true)}>
                Create Your Organization
              </Button>
            </Empty>
          ) : (
            <List
              dataSource={organizations}
              renderItem={(org) => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      onClick={() => navigate(`/organizations/${org.id}`)}
                    >
                      Manage
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 8,
                          background: '#1890ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: 20,
                          fontWeight: 600,
                        }}
                      >
                        {org.name.charAt(0).toUpperCase()}
                      </div>
                    }
                    title={
                      <Space>
                        {org.name}
                        <Tag color={org.role === 'OWNER' ? 'gold' : org.role === 'ADMIN' ? 'blue' : 'default'}>
                          {org.role}
                        </Tag>
                      </Space>
                    }
                    description={org.description || 'No description'}
                  />
                </List.Item>
              )}
            />
          )}
        </Card>

        <Modal
          title="Create Organization"
          open={modalOpen}
          onCancel={() => {
            setModalOpen(false);
            form.resetFields();
          }}
          footer={null}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreate}
            requiredMark={false}
          >
            <Form.Item
              name="name"
              label="Organization Name"
              rules={[
                { required: true, message: 'Please enter organization name' },
                { min: 3, message: 'Name must be at least 3 characters' },
              ]}
            >
              <Input placeholder="e.g., Acme Corp" />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description (Optional)"
            >
              <Input.TextArea
                rows={3}
                placeholder="Brief description of your organization"
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={creating}>
                  Create
                </Button>
                <Button onClick={() => {
                  setModalOpen(false);
                  form.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Space>
  );
}
